import { Key, KeyboardEvent, ReactNode, useMemo } from 'react';
import { Link } from 'react-router';
import { Text } from '@connect-x-app/hybrid-ui';
import { MessageMention } from '@connect-x-app/shared-domain-models';
import { FALLBACK_REGION_CODE } from '@connect-x-app/shared-translations';
import { COLORS } from '@connect-x-app/shared-ui';
import { getArrayOfTextSplitByMention, getLinkMatches } from '@connect-x-app/shared-utils';
import classnames from 'classnames';

import './messageBubbleText.scss';

type Props = {
  disabled?: boolean;
  isMe: boolean;
  mentions: MessageMention[];
  setActiveMention: (mention: MessageMention) => void;
  text?: string | null;
  withStatus: boolean;
};

export const MessageBubbleText = ({ disabled = false, isMe, mentions, setActiveMention, text, withStatus }: Props) => {
  const textColor = isMe ? COLORS.zulu.$08 : COLORS.zulu.$10;

  const renderLink = (href: string, linkText: string, key: Key) => {
    if ((href || '').trim().toLowerCase().startsWith('javascript:')) {
      return (
        <span aria-disabled="true" className="message-bubble-hyperlink-text" key={key}>
          {linkText}
        </span>
      );
    }

    return (
      <Link className="message-bubble-hyperlink-text" key={key} rel="noopener noreferrer" target="_blank" to={href}>
        {linkText}
      </Link>
    );
  };

  const onMentionKeyDown = (event: KeyboardEvent<HTMLSpanElement>, mention: MessageMention) => {
    if (disabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setActiveMention(mention);
    }
  };

  const mentionLabels = useMemo(() => mentions.map(mention => mention.label), [mentions]);

  const regionCode = useMemo(() => {
    // format of navigator.language is: en-US, nl-BE, ...
    const splittedArray = navigator.language.split('-');
    if (splittedArray.length > 1) {
      return splittedArray[1];
    }

    return FALLBACK_REGION_CODE;
  }, []);

  const renderTextWithLinks = (part: string, partKey: number): ReactNode => {
    const matches = disabled ? [] : getLinkMatches(part, regionCode);

    if (!matches.length) {
      return <span key={partKey}>{part}</span>;
    }

    const nodes: ReactNode[] = [];
    let lastIndex = 0;

    matches.forEach((match, matchIndex) => {
      if (match.index > lastIndex) {
        nodes.push(part.substring(lastIndex, match.index));
      }

      nodes.push(renderLink(match.url, match.text, `${partKey}-link-${matchIndex}`));
      lastIndex = match.lastIndex;
    });

    if (lastIndex < part.length) {
      nodes.push(part.substring(lastIndex));
    }

    return <span key={partKey}>{nodes}</span>;
  };

  return (
    <Text
      className={classnames({
        'slide-left': withStatus,
      })}
      color={textColor}
      size="SM"
    >
      {getArrayOfTextSplitByMention(text, mentionLabels)?.map((part, index) => {
        const mention = mentions.find(mention => `@${mention.label}` === part);

        if (mention) {
          // Rendered as a span instead of a native button: browsers exclude button
          // content from text selection, which broke copying messages with mentions.
          return (
            <span
              aria-disabled={disabled}
              className="clickable-mention"
              key={`${mention.label}-${index}`}
              onClick={disabled ? undefined : () => setActiveMention(mention)}
              onKeyDown={event => onMentionKeyDown(event, mention)}
              role="button"
              tabIndex={disabled ? -1 : 0}
            >
              <Text as="span" className="message-bubble-mention-role" weight="bold">
                {part}&nbsp;
              </Text>
            </span>
          );
        } else {
          return renderTextWithLinks(part, index);
        }
      })}
    </Text>
  );
};
